import type { ISkillResource, ISkillItem } from "../../skills";
import { PeriodParam } from "../../types/PeriodParam";
import type { IUserResource } from "../../users";
import {
    createApiQueryHook,
    type IApiQueryFunction,
    type IOrderable,
    type IPaginateable,
    mapOrder,
    mapPagination,
} from "../../utils/api";
import { mapRelationalFilter, type RelationalFilter } from "../../utils/filters";
import { serialize } from "../../utils/url";
import type { IFollowUpList } from "../types/IFollowUpList";

export interface IFollowUpListQueryParameters
    extends IPaginateable, IOrderable<"createdAt" | "periodStart" | "periodEnd"> {
    filters?: {
        learner?: RelationalFilter<IUserResource>;
        createdBy?: RelationalFilter<IUserResource>;
        title?: string;
        skill?: RelationalFilter<ISkillItem>;
        periodStart?: {
            date?: string;
            param?: PeriodParam;
        };
        periodEnd?: {
            date?: string;
            param?: PeriodParam;
        };
        availableSkill?: RelationalFilter<ISkillResource>;
        intervalStart?: string;
        intervalEnd?: string;
    };
}

export type IFollowUpListQueryResult = IFollowUpList;

const mapFilters = (filters: IFollowUpListQueryParameters["filters"]) => {
    const { learner, createdBy, title, skill, periodStart, periodEnd, availableSkill, intervalStart, intervalEnd } =
        filters ?? {};

    return {
        "learner.uuid": mapRelationalFilter(learner),
        "createdBy.uuid": mapRelationalFilter(createdBy),
        "briefs.skillLevels.skill.uuid": mapRelationalFilter(skill),
        "availableSkill.uuid": mapRelationalFilter(availableSkill),
        [`periodStart[${periodStart?.param ?? PeriodParam.BEFORE}]`]: periodStart?.date,
        [`periodEnd[${periodEnd?.param ?? PeriodParam.BEFORE}]`]: periodEnd?.date,
        title,
        intervalStart,
        intervalEnd,
    };
};

export const followUpListQuery: IApiQueryFunction<IFollowUpListQueryParameters> = ({ pagination, filters, order }) => ({
    method: "GET",
    url: `/follow_ups${serialize(
        {
            ...mapFilters(filters),
            ...mapPagination(pagination),
            ...mapOrder(order),
        },
        true,
    )}`,
});

export const useFollowUpListQuery = createApiQueryHook<IFollowUpListQueryParameters, IFollowUpListQueryResult>(
    followUpListQuery,
);
